import {
  AttendanceVerificationMethod,
  type AttendanceVerificationMethodConfig,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { type CbhAppInfo, useAppInfo } from "@src/appV2/lib/index";
import { isEnabledForAppVersionAndOta } from "@src/appV2/lib/utils/isEnabledForAppVersionAndOta";
import type { ShiftStateMetadata } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { useCallback, useMemo } from "react";

const SUPPORTED_ATTENDANCE_VERIFICATION_METHODS = new Set<AttendanceVerificationMethod>([
  AttendanceVerificationMethod.GEOFENCE,
  AttendanceVerificationMethod.NFC_TAG,
  AttendanceVerificationMethod.UKG_INTEGRATION,
  AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION,
  AttendanceVerificationMethod.MESH_INTEGRATION,
  AttendanceVerificationMethod.TAKE_A_VIDEO,
  AttendanceVerificationMethod.AI_TAKE_A_VIDEO,
]);

interface MethodVersionConfig {
  minimalOtaVersion: string | number;
  minimalStoreVersion: string;
  state: "enabled" | "disabled";
}

type MethodsVersions = Partial<Record<string, MethodVersionConfig>>;

function filterMethodsByVersion(
  methods: AttendanceVerificationMethodConfig[],
  methodsConfiguration: MethodsVersions | undefined,
  appInfo: CbhAppInfo | undefined
): AttendanceVerificationMethodConfig[] {
  if (!isDefined(methodsConfiguration)) {
    return methods;
  }

  return methods.filter((method) => {
    const methodVersion = methodsConfiguration[method.type];
    if (!isDefined(methodVersion)) {
      return false;
    }

    if (methodVersion.state === "disabled") {
      return false;
    }

    return isEnabledForAppVersionAndOta({
      minimalOtaVersion: methodVersion.minimalOtaVersion,
      minimalStoreVersion: methodVersion.minimalStoreVersion,
      appInfo,
    });
  });
}

interface UseAttendanceVerificationMethodParams {
  shiftStateMetadata?: ShiftStateMetadata;
}

interface UseAttendanceVerificationMethodResult {
  isMethodEnabled: (method: AttendanceVerificationMethod) => boolean;
  getMethodConfig: <T extends AttendanceVerificationMethodConfig>(
    method: AttendanceVerificationMethod
  ) => T | undefined;
  attendanceVerificationMethods: AttendanceVerificationMethodConfig[];
  externalTimeclockCredentials: {
    badgeNumber: string | undefined;
    pin: string | undefined;
  };
}

export function useAttendanceVerificationMethod(
  params: UseAttendanceVerificationMethodParams
): UseAttendanceVerificationMethodResult {
  const { shiftStateMetadata } = params;

  const { appInfo } = useAppInfo();

  const attendanceVerificationMethodsRollout = useCbhFlag(
    CbhFeatureFlag.ATTENDANCE_TRACKING_METHODS_ROLLOUT,
    {
      defaultValue: {
        state: "off" as const,
        minimalOtaVersion: "*",
        minimalStoreVersion: "*",
        methodsConfiguration: undefined,
      },
    }
  );

  const { methodsConfiguration } = attendanceVerificationMethodsRollout;

  const enabledMethods = useMemo(() => {
    const supportedMethods = shiftStateMetadata?.attendanceVerificationMethods?.filter(
      (method) => method.isEnabled && SUPPORTED_ATTENDANCE_VERIFICATION_METHODS.has(method.type)
    );

    const methods: AttendanceVerificationMethodConfig[] =
      !isDefined(supportedMethods) || supportedMethods.length === 0
        ? [{ type: AttendanceVerificationMethod.GEOFENCE, isEnabled: true }]
        : supportedMethods;

    return filterMethodsByVersion(methods, methodsConfiguration, appInfo);
  }, [shiftStateMetadata?.attendanceVerificationMethods, methodsConfiguration, appInfo]);

  const externalTimeclockCredentials = {
    badgeNumber: shiftStateMetadata?.externalTimeclockCredentials?.badgeNumber,
    pin: shiftStateMetadata?.externalTimeclockCredentials?.pin,
  };

  const isMethodEnabled = useCallback(
    (method: AttendanceVerificationMethod): boolean => {
      return enabledMethods.some(
        (methodConfig) => methodConfig.type === method && methodConfig.isEnabled
      );
    },
    [enabledMethods]
  );

  const getMethodConfig = useCallback(
    <T extends AttendanceVerificationMethodConfig>(
      method: AttendanceVerificationMethod
    ): T | undefined => {
      return enabledMethods.find((methodConfig) => methodConfig.type === method) as T | undefined;
    },
    [enabledMethods]
  );

  return {
    attendanceVerificationMethods: enabledMethods,
    isMethodEnabled,
    getMethodConfig,
    externalTimeclockCredentials,
  };
}
